import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { apiService } from '../services/api';

interface TableViewProps {
  filePath: string | null;              // absolute path to xlsx
  templateBillPath?: string | null;     // formatted invoice path (preferred for download)
  mode: 'primary' | 'secondary' | 'merged' | 'bill' | 'preview';
  onConfirmSelections?: (rows: any[]) => void;
  /** preview: чем разобрали файл — показываем пользователю */
  ingestMethod?: string | null;
  /** preview: предупреждения разбора */
  ingestWarnings?: string[];
  /** preview: пользователь подтвердил распознанное */
  onConfirmPreview?: (rows: any[]) => void;
  isBusy?: boolean;
}

// В файле распознанного колонки технические — показываем по-русски
const PREVIEW_COLUMN_LABELS: Record<string, string> = {
  name: 'Наименование',
  qty: 'Количество',
  unit: 'Ед. изм.',
};

const TableView: React.FC<TableViewProps> = ({
  filePath, templateBillPath, mode, onConfirmSelections,
  ingestMethod, ingestWarnings, onConfirmPreview, isBusy,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selections, setSelections] = useState<Record<number, string>>({});
  const [editingCell, setEditingCell] = useState<{row: number, col: string} | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fixedBarContainerRef = useRef<HTMLDivElement>(null);
  const fixedBarScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      if (!filePath) { setRows([]); setColumns([]); return; }
      try {
        setLoading(true); setError(null);
        const blob = await apiService.downloadFile(filePath);
        const arrayBuffer = await blob.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        setRows(json);
        setColumns(json.length ? Object.keys(json[0]) : []);
      } catch (e) {
        setError('Не удалось загрузить таблицу');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filePath]);

  // Parse options for secondary mode: prefer top_10_matches (JSON), fallback to split("Наименование из базы")
  const optionsByRowId = useMemo(() => {
    if (mode !== 'secondary') return {} as Record<number, string[]>;
    const map: Record<number, string[]> = {};
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const rid = typeof r.row_id === 'number' ? r.row_id : i;
      try {
        const raw = (r as any).top_10_matches;
        let options: string[] = [];
        if (raw !== undefined && raw !== null) {
          const list = typeof raw === 'string' ? JSON.parse(raw) : Array.isArray(raw) ? raw : [];
          options = Array.from(new Set(list.map((x: any) => String(x?.raw_name || '')))).filter(Boolean) as string[];
        }
        if ((!options || options.length === 0) && typeof r['Наименование из базы'] === 'string') {
          const cell = String(r['Наименование из базы']);
          const DELIM = '⟬SEP⟭';
          const pieces = cell.includes(DELIM) ? cell.split(DELIM) : cell.split('; ');
          const split = pieces.map(s => s.trim()).filter(Boolean);
          options = Array.from(new Set(split));
        }
        if (options && options.length) {
          map[rid] = options;
        }
      } catch { /* ignore */ }
    }
    return map;
  }, [rows, mode]);

  const totalWithOptions = useMemo(() => Object.keys(optionsByRowId).length, [optionsByRowId]);
  const selectedCount = useMemo(() => Object.keys(selections).length, [selections]);

  // Определяем проблемные строки для режима 'bill'
  const problemRows = useMemo(() => {
    if (mode !== 'bill') return new Set<number>();
    const problems = new Set<number>();
    rows.forEach((row, idx) => {
      const status = String(row['Статус проверки'] || '').toLowerCase();
      if (status.includes('несоответствие') || status.includes('ошибка') || status.includes('не найден') || status.includes('отсутствует')) {
        problems.add(idx);
      }
    });
    return problems;
  }, [rows, mode]);

  const handleCellChange = (rowIndex: number, col: string, value: string) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row[col] = value;
      next[rowIndex] = row;
      return next;
    });
  };

  const startEditing = (rowIndex: number, col: string) => {
    setEditingCell({ row: rowIndex, col });
  };

  const stopEditing = () => {
    setEditingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, col: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      stopEditing();
    } else if (e.key === 'Escape') {
      stopEditing();
    }
  };

  const addRow = (afterIndex?: number) => {
    const template: any = {};
    for (const c of columns) template[c] = '';
    setRows(prev => {
      if (afterIndex !== undefined) {
        const next = [...prev];
        next.splice(afterIndex + 1, 0, template);
        return next;
      }
      return [...prev, template];
    });
  };

  const deleteRow = (rowIndex: number) => {
    if (rows.length > 1) {
      setRows(prev => prev.filter((_, i) => i !== rowIndex));
    }
  };

  // Prefill default selections with the first option per row
  useEffect(() => {
    if (mode !== 'secondary') return;
    const next: Record<number, string> = { ...selections };
    let changed = false;
    for (const [ridStr, opts] of Object.entries(optionsByRowId)) {
      const rid = Number(ridStr);
      if (!(rid in next) && opts && opts.length) {
        next[rid] = opts[0];
        changed = true;
      }
    }
    if (changed) setSelections(next);
  }, [mode, optionsByRowId]);

  const handleSave = () => {
    if (!onConfirmSelections) return;
    // Build outgoing rows with single value in 'Наименование из базы'
    const rowsForSave = rows.map((r, idx) => {
      const rid = typeof r.row_id === 'number' ? r.row_id : idx;
      const opts = (optionsByRowId as any)[rid] as string[] | undefined;
      const selected = selections[rid] ?? (opts && opts.length ? opts[0] : String(r['Наименование из базы'] || ''));
      const updated: any = { ...r, row_id: rid };
      if (mode === 'secondary') {
        updated['Наименование из базы'] = selected;
      }
      return updated;
    });
    onConfirmSelections(rowsForSave);
  };

  const handleConfirmPreview = () => {
    if (!onConfirmPreview) return;
    const payload = rows
      .map((r) => ({
        name: String(r.name ?? '').trim(),
        qty: r.qty === '' || r.qty === null || r.qty === undefined ? null : r.qty,
        unit: r.unit === '' || r.unit === null || r.unit === undefined ? null : String(r.unit),
      }))
      .filter((r) => r.name.length > 0);
    if (!payload.length) return;
    onConfirmPreview(payload);
  };

  const validPreviewRows = useMemo(
    () => (mode === 'preview' ? rows.filter((r) => String(r.name ?? '').trim().length > 0).length : 0),
    [rows, mode],
  );

  // Create a single fixed-position horizontal scrollbar synced with the table
  useEffect(() => {
    const main = scrollContainerRef.current;
    const container = fixedBarContainerRef.current;
    const bar = fixedBarScrollRef.current;
    if (!main || !container || !bar) return;

    let isSyncing = false;
    const onMainScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      bar.scrollLeft = main.scrollLeft;
      isSyncing = false;
    };
    const onBarScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      main.scrollLeft = bar.scrollLeft;
      isSyncing = false;
    };

    const updateBarGeometry = () => {
      const rect = main.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
      container.style.display = visible ? 'block' : 'none';
      if (!visible) return;
      const left = Math.max(0, rect.left);
      const right = Math.min(window.innerWidth, rect.right);
      const width = Math.max(0, right - left);
      container.style.left = `${left}px`;
      container.style.width = `${width}px`;
      const inner = bar.firstElementChild as HTMLDivElement | null;
      if (inner) inner.style.width = `${main.scrollWidth}px`;
      bar.scrollLeft = main.scrollLeft;
    };

    updateBarGeometry();
    main.addEventListener('scroll', onMainScroll, { passive: true });
    bar.addEventListener('scroll', onBarScroll, { passive: true });
    window.addEventListener('resize', updateBarGeometry);
    window.addEventListener('scroll', updateBarGeometry, { passive: true });
    const ro = new ResizeObserver(updateBarGeometry);
    ro.observe(main);

    return () => {
      main.removeEventListener('scroll', onMainScroll);
      bar.removeEventListener('scroll', onBarScroll);
      window.removeEventListener('resize', updateBarGeometry);
      window.removeEventListener('scroll', updateBarGeometry);
      ro.disconnect();
    };
  }, [rows, columns]);

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="flex items-center justify-between p-4 bg-white border-b shadow-sm">
        <div className="text-sm font-medium text-gray-900">
          {mode === 'preview' && (
            <div>
              <div>Проверьте, что мы распознали</div>
              {ingestMethod && (
                <div className="text-xs font-normal text-gray-500 mt-0.5">{ingestMethod}</div>
              )}
            </div>
          )}
          {mode === 'secondary' && 'Результат поиска'}
          {mode === 'merged' && 'Итоговая таблица'}
          {mode === 'bill' && '💰 Итоговый счет'}
        </div>
        <div className="flex gap-2">
          {mode === 'preview' && (
            <button
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
              disabled={!validPreviewRows || !!isBusy}
              onClick={handleConfirmPreview}
              title="Запустить поиск позиций по вашему прайсу"
            >
              {isBusy ? 'Запускаем...' : `Всё верно — искать в прайсе (${validPreviewRows})`}
            </button>
          )}
          {mode === 'secondary' && (
            <button 
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed" 
              disabled={selectedCount < totalWithOptions} 
              onClick={handleSave}
            >
              Сохранить
            </button>
          )}
          {mode === 'bill' && (
            <button 
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
              onClick={async () => {
                const downloadPath = templateBillPath || filePath;
                if (downloadPath) {
                  try {
                    const blob = await apiService.downloadFile(downloadPath);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Счет_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error('Ошибка скачивания:', error);
                  }
                }
              }}
            >
              {templateBillPath ? '📄 Скачать счет (по шаблону)' : '📄 Скачать счет'}
            </button>
          )}
        </div>
      </div>
      {mode === 'preview' && (
        <div className="px-4 pt-3">
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-medium">Поиск по прайсу запустится только после вашей проверки.</p>
            <p className="mt-0.5">
              Наименование, количество и единицы можно править прямо в таблице,
              лишние строки — удалять кнопкой «−».
            </p>
            {ingestWarnings && ingestWarnings.length > 0 && (
              <ul className="list-disc list-outside ml-4 mt-1 space-y-0.5">
                {ingestWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-500 text-sm">Загрузка...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-500 text-sm">Нет данных</div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border overflow-y-auto overflow-x-hidden pb-8" ref={scrollContainerRef}>
            <table className="min-w-full table-auto">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
                <tr>
                  <th className="w-8 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  {columns.map((c) => (
                    <th key={c} className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${c === 'Наименование из базы' ? 'w-64' : ''}`}>
                      {mode === 'preview' ? (PREVIEW_COLUMN_LABELS[c] || c) : c}
                    </th>
                  ))}
                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((r, idx) => {
                  const rid = typeof r.row_id === 'number' ? r.row_id : idx;
                  const opts = (optionsByRowId as any)[rid] as string[] | undefined;
                  const isProblemRow = problemRows.has(idx);
                  const rowClassName = isProblemRow 
                    ? "hover:bg-red-50 transition-colors duration-150 bg-red-25 border-l-4 border-red-400" 
                    : "hover:bg-gray-50 transition-colors duration-150";
                  return (
                    <tr key={rid} className={rowClassName}>
                      <td className="px-3 py-2 text-xs text-gray-500 font-mono">
                        {idx + 1}
                      </td>
                      {columns.map((c) => {
                        if (mode === 'secondary' && c === 'Наименование из базы') {
                          const selectedValue = selections[rid] ?? (opts?.[0] ?? '');
                          return (
                            <td key={c} className="px-4 py-2" style={{ width: '250px', maxWidth: '250px' }}>
                              {opts && opts.length ? (
                                <div className="relative">
                                  <select
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 appearance-none pr-8"
                                    value={selectedValue}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      // Храним выбор только в selections. НЕ пишем в ячейку
                                      // «Наименование из базы» — она источник вариантов (фолбэк),
                                      // перезапись схлопывала список до одного варианта.
                                      setSelections((prev) => ({ ...prev, [rid]: val }));
                                    }}
                                  >
                                    {opts.map((o) => (
                                      <option key={o} value={o}>{o}</option>
                                    ))}
                                  </select>
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </div>
                              ) : (
                                <div className="px-3 py-2 text-gray-400 text-sm italic text-center">—</div>
                              )}
                            </td>
                          );
                        }
                        
                        const isEditing = editingCell?.row === idx && editingCell?.col === c && mode !== 'bill';
                        const cellValue = String(r[c] ?? '');
                        
                        return (
                          <td key={c} className="px-4 py-2 align-top min-w-0 max-w-xs">
                            {isEditing ? (
                              <input
                                type="text"
                                className="w-full min-h-[2rem] border border-indigo-500 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                value={cellValue}
                                onChange={(e) => handleCellChange(idx, c, e.target.value)}
                                onBlur={stopEditing}
                                onKeyDown={(e) => handleKeyDown(e, idx, c)}
                                autoFocus
                              />
                            ) : (
                              <div
                                className={`px-3 py-2 text-sm rounded-md transition-colors duration-200 min-h-[2rem] flex items-center whitespace-pre-wrap break-words ${
                                  mode === 'bill' ? 'cursor-default' : 'cursor-pointer hover:bg-gray-100'
                                }`}
                                onClick={() => mode !== 'bill' && startEditing(idx, c)}
                                title={mode === 'bill' ? '' : 'Нажмите для редактирования'}
                              >
                                {cellValue || <span className="text-gray-400 italic">{mode === 'bill' ? '—' : 'Введите значение...'}</span>}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 align-top text-center">
                        {mode !== 'bill' && (
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              className="w-7 h-7 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-200" 
                              onClick={() => addRow(idx)}
                              title="Добавить строку после"
                            >
                              +
                            </button>
                            <button 
                              className="w-7 h-7 bg-red-100 hover:bg-red-200 text-red-700 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed" 
                              onClick={() => deleteRow(idx)}
                              disabled={rows.length <= 1}
                              title="Удалить строку"
                            >
                              −
                            </button>
                          </div>
                        )}
                        {mode === 'bill' && isProblemRow && (
                          <div className="text-red-500 text-xs font-medium">⚠️</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Fixed horizontal scrollbar aligned to the table pane */}
      <div
        ref={fixedBarContainerRef}
        style={{ position: 'fixed', bottom: 8, left: 0, width: '0px', zIndex: 50 }}
      >
        <div
          ref={fixedBarScrollRef}
          className="overflow-x-auto overflow-y-hidden"
          style={{ height: 12, background: 'rgba(255,255,255,0.9)', borderTop: '1px solid #e5e7eb' }}
        >
          <div style={{ height: 1 }} />
        </div>
      </div>
    </div>
  );
};

export default TableView;


